import { API_CONFIG, buildApiUrl } from '../config/api.config';
import type { ResultEntity } from './api';

// API Client with automatic token handling and interceptors
class ApiClient {
  private static instance: ApiClient;
  
  private constructor() {}
  
  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }
  
  // Get default headers with auth token
  private getHeaders(includeAuth: boolean = true, customHeaders?: HeadersInit, method?: string): HeadersInit {
    const headers: Record<string, string> = {
      'x-tenant-key': API_CONFIG.TENANT_KEY,
    };
    
    // Only set Content-Type for requests that typically have a body
    if (method !== 'DELETE' && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }
    
    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    // Merge with custom headers
    if (customHeaders) {
      Object.assign(headers, customHeaders);
    }
    
    return headers;
  }
  
  // Handle token expiration
  private handleUnauthorized(): void {
    // Clear auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    
    // Dispatch a custom event that the AuthContext can listen to
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
    
    // Redirect to login if on a protected route
    if (window.location.pathname !== '/' && !window.location.pathname.includes('/auth')) {
      window.location.href = '/';
    }
  }
  
  // Extract error message from various response formats
  private extractErrorMessage(result: any): string {
    if (result.error) {
      if (typeof result.error === 'object' && result.error.error) {
        return result.error.error;
      } else if (typeof result.error === 'string') {
        return result.error;
      }
    }
    if (result.message) {
      return result.message;
    }
    return 'Request failed';
  }
  
  // Generic request method
  async request<T>(
    endpoint: string,
    options: RequestInit & { includeAuth?: boolean } = {}
  ): Promise<T> {
    const { includeAuth = true, ...fetchOptions } = options;
    const url = buildApiUrl(endpoint);
    const method = fetchOptions.method || 'GET';
    
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers: this.getHeaders(includeAuth, fetchOptions.headers, method),
      });
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication required');
      }
      
      const result: ResultEntity<T> = await response.json();
      
      if (!response.ok || !result.success) {
        const errorMessage = this.extractErrorMessage(result);
        throw new Error(errorMessage);
      }
      
      return result.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }
  
  // Convenience methods
  async get<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      includeAuth,
    });
  }
  
  async post<T>(endpoint: string, body?: any, includeAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      includeAuth,
    });
  }
  
  async put<T>(endpoint: string, body?: any, includeAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      includeAuth,
    });
  }
  
  async patch<T>(endpoint: string, body?: any, includeAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      includeAuth,
    });
  }
  
  async delete<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      includeAuth,
    });
  }
  
  // File upload method
  async upload<T>(endpoint: string, formData: FormData, includeAuth: boolean = true): Promise<T> {
    const url = buildApiUrl(endpoint);
    
    try {
      const headers: Record<string, string> = {
        'x-tenant-key': API_CONFIG.TENANT_KEY,
      };
      
      if (includeAuth) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication required');
      }
      
      const result: ResultEntity<T> = await response.json();
      
      if (!response.ok || !result.success) {
        const errorMessage = this.extractErrorMessage(result);
        throw new Error(errorMessage);
      }
      
      return result.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();

// Export for backwards compatibility
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  includeAuth: boolean = false
): Promise<T> => {
  return apiClient.request<T>(endpoint, { ...options, includeAuth });
};